import type { Room, RoomConnectOptions } from 'livekit-client';
import { ConnectionState, DisconnectReason } from 'livekit-client';
import { applyParticipantTokenRefresh } from '@/lib/livekit-participant-token-refresh';
import type { ConnectionDetails } from '@/lib/types';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function isRoomConnected(room: Room): boolean {
  return room.state === ConnectionState.Connected;
}

/** Erros de rede/sinalização onde retry com back-off pode recuperar a entrada. */
export function isSignalOrNetworkConnectError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error ?? '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('signal connection') ||
    message.includes('could not establish') ||
    message.includes('networkerror') ||
    message.includes('network error') ||
    message.includes('twirp') ||
    message.includes('timeout') ||
    message.includes('connection refused')
  );
}

/** Motivos em que uma reconexão manual ainda faz sentido após o SDK esgotar retries. */
export function isManualReconnectReason(reason?: DisconnectReason): boolean {
  if (reason === undefined) return true;
  if (reason === DisconnectReason.CLIENT_INITIATED) return false;
  if (reason === DisconnectReason.DUPLICATE_IDENTITY) return false;
  if (reason === DisconnectReason.PARTICIPANT_REMOVED) return false;
  if (reason === DisconnectReason.ROOM_DELETED) return false;
  return true;
}

export async function connectRoomWithRetry(params: {
  room: Room;
  serverUrl: string;
  token: string;
  connectOptions?: RoomConnectOptions;
  maxAttempts?: number;
  initialDelayMs?: number;
  fetchFreshToken?: () => Promise<{ serverUrl: string; participantToken: string }>;
}): Promise<void> {
  const {
    room,
    connectOptions,
    maxAttempts = 4,
    initialDelayMs = 1000,
    fetchFreshToken,
  } = params;
  let serverUrl = params.serverUrl;
  let token = params.token;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      await room.connect(serverUrl, token, connectOptions);
      return;
    } catch (error) {
      const isLastAttempt = attempt >= maxAttempts - 1;
      if (!isSignalOrNetworkConnectError(error) || isLastAttempt) {
        throw error;
      }

      const delay = initialDelayMs * 2 ** attempt;
      console.warn(
        `[meet-connect] tentativa ${attempt + 1}/${maxAttempts} falhou — retry em ${delay}ms`,
        error,
      );
      await sleep(delay);

      if (fetchFreshToken) {
        try {
          const fresh = await fetchFreshToken();
          serverUrl = fresh.serverUrl;
          token = fresh.participantToken;
        } catch (tokenError) {
          console.warn('[meet-connect] falha ao renovar token antes do retry:', tokenError);
        }
      }
    }
  }
}

export async function attemptMeetManualReconnect(params: {
  room: Room;
  connectionDetails: ConnectionDetails;
  connectOptions: RoomConnectOptions;
  fetchFreshToken: () => Promise<ConnectionDetails>;
  waitMs?: number;
}): Promise<boolean> {
  const { room, connectOptions, fetchFreshToken, waitMs = 12_000 } = params;

  if (isRoomConnected(room)) {
    return true;
  }

  try {
    const fresh = await fetchFreshToken();
    await applyParticipantTokenRefresh(
      room,
      fresh.serverUrl,
      fresh.participantToken,
      connectOptions,
    );

    const deadline = Date.now() + waitMs;
    while (Date.now() < deadline) {
      if (isRoomConnected(room)) {
        return true;
      }
      await sleep(250);
    }
  } catch (error) {
    console.warn('[meet-reconnect] falha na reconexão manual:', error);
  }

  return isRoomConnected(room);
}
